import type {
  ProfileCrossing,
  ProfileStickInput,
  ProfileFault,
  ProfilePoint,
  ProfileSeries,
  ProfileVertex,
} from './profile-plan';
import type {
  CrossSectionRequest,
  SectionViewport,
  CrossSectionResponse,
  CrossSectionSubject,
  CulturalBoundaryValue,
  FaultValue,
  GroundSurfaceValue,
  HorizonValue,
  SectionMode,
  SectionObject,
  WellValue,
} from './types';

/** A horizon with its id kept, since the id is the grid it came from. */
export interface GroupedHorizon extends HorizonValue {
  gridId: string;
}

/** A crossing well with its id kept — the uwi14, null for a pre-drill permit. */
export interface GroupedWell extends WellValue {
  id: string | null;
}

/** A fault span with its id kept — the area geometry uuid. */
export interface GroupedFault extends FaultValue {
  id: string;
}

/** A county crossing with whatever id it had, which may be none. */
export interface GroupedBoundary extends CulturalBoundaryValue {
  id: string | null;
}

/**
 * The section's objects split by kind, which is the shape every surface actually
 * wants — the chart needs the horizon stack, the wells list needs the crossings.
 */
export interface GroupedResults {
  /** In stratigraphic order, as `positionalIndex` gives it. */
  horizons: GroupedHorizon[];
  /** One series, or null when ground surface was not requested. */
  groundSurface: GroundSurfaceValue | null;
  /** Ordered by crossing, so a well crossing twice reads left to right. */
  wells: GroupedWell[];
  faults: GroupedFault[];
  /** Where the plane crosses a county line, in along-line order. */
  boundaries: GroupedBoundary[];
}

/**
 * Sorts a null stratigraphic index last rather than letting it coerce to 0.
 *
 * @param {number | null} positionalIndex - The grid's stratigraphic index.
 * @return {number} A sortable rank, with nulls at the bottom of the stack.
 */
const stratigraphicRank = (positionalIndex: number | null): number =>
  positionalIndex ?? Number.POSITIVE_INFINITY;

/**
 * Whether a request is complete enough for the backend to accept it — two points
 * either way: two vertices for a drawn line, two wells for a traverse. Kept here so every call site
 * shares one answer instead of each guarding the plane's validity in prose.
 *
 * @param {CrossSectionRequest | null} request - The candidate request.
 * @return {boolean} True when the request can be sent.
 */
export const isFetchableRequest = (
  request: CrossSectionRequest | null
): request is CrossSectionRequest => {
  if (!request) {
    return false;
  }
  // Two either way: a plane needs two points, and in Well to Well the wells ARE the
  // points. One well is refused by the backend deep in the line builder — with a
  // message about vertices, which is not what the user did — so it never goes out.
  return request.mode === 'arbitrary_line'
    ? (request.vertices?.length ?? 0) >= 2
    : (request.wellUwis?.length ?? 0) >= 2;
};

/**
 * Whether a well crossing belongs on the section at all, given how the section was
 * built.
 *
 * A vertical crossing an Arbitrary Line is dropped. A vertical hole occupies one
 * surface location, so the plane meets it at a single point — and a single dot on a
 * profile reads as "the wellbore is at this depth and nowhere else", which the
 * geometry does not say. Drawing it honestly means a stick from KB to toe, and that
 * is a story of its own; until it lands, product's call is to leave the well off
 * rather than to state something false about it.
 *
 * Well to Well is untouched: there the verticals ARE the section — they are the
 * vertices the user chose, and the line is defined by standing at each one. A Well
 * Workspace's own edit of a vertical is kept too: it is the edit under review.
 *
 * @param {WellValue} well - The crossing.
 * @param {SectionMode} mode - How the section was built.
 * @return {boolean} True when the crossing should be shown.
 */
const crossingBelongs = (well: WellValue, mode: SectionMode): boolean =>
  mode !== 'arbitrary_line' ||
  !well.isVertical ||
  well.workspaceEdited === true;

/**
 * Splits `results` into the groups the UI renders from.
 *
 * Unknown `type`s are dropped rather than throwing, which is the forward-compatible
 * half of the contract: the backend can add an object type — units, type-curve
 * areas — and an older client keeps working instead of failing on a row it has
 * never heard of.
 *
 * `mode` is required rather than optional because one group depends on it: a caller
 * that forgot to pass it would silently put verticals back on an Arbitrary Line,
 * which is the thing the gate exists to prevent.
 *
 * @param {SectionObject[] | undefined} results - The tagged rows from the response.
 * @param {SectionMode} mode - How the section was built, which decides whether a
 *   vertical crossing belongs on it.
 * @return {GroupedResults} The rows grouped by kind, each group sorted for display.
 */
export const groupSectionResults = (
  results: SectionObject[] | undefined,
  mode: SectionMode
): GroupedResults => {
  const grouped: GroupedResults = {
    horizons: [],
    groundSurface: null,
    wells: [],
    faults: [],
    boundaries: [],
  };

  for (const row of results ?? []) {
    switch (row.type) {
      case 'horizon':
        grouped.horizons.push({ ...row.value, gridId: row.id });
        break;
      case 'ground_surface':
        // One row per cut; a second would mean the backend changed shape, so the
        // last one wins rather than silently merging two series.
        grouped.groundSurface = row.value;
        break;
      case 'well':
        if (crossingBelongs(row.value, mode)) {
          grouped.wells.push({ ...row.value, id: row.id });
        }
        break;
      case 'fault':
        grouped.faults.push({ ...row.value, id: row.id });
        break;
      case 'cultural_boundary':
        grouped.boundaries.push({ ...row.value, id: row.id });
        break;
      default:
        // An object type this client does not know. Ignored on purpose.
        break;
    }
  }

  // Stratigraphic order is the backend's to state, so sort by what it sent rather than
  // re-deriving it. Nulls last: a bare subtraction would coerce null to 0 and float an
  // intervalless grid to the top. Array#sort is stable, so ties keep their arrival order.
  grouped.horizons.sort(
    (a, b) =>
      stratigraphicRank(a.positionalIndex) -
      stratigraphicRank(b.positionalIndex)
  );
  grouped.wells.sort(
    (a, b) => a.xOffsetFt - b.xOffsetFt || a.crossingIndex - b.crossingIndex
  );
  grouped.faults.sort((a, b) => a.startFt - b.startFt);
  grouped.boundaries.sort((a, b) => a.xFt - b.xFt);

  return grouped;
};

/**
 * Whether a response still describes what is on screen. Both keys have to match:
 * moving the line changes `sectionGeometryId`, while changing the grid stack leaves
 * it alone and changes `objectSetId` — so checking only the first would let a stale
 * horizon set look current.
 *
 * A response can only be confirmed against something, so this is false until the
 * caller holds both ids. Adopting a first response is the caller's decision, not
 * something to launder through a freshness check.
 *
 * @param {CrossSectionResponse | undefined} response - The response to test.
 * @param {string | null} sectionGeometryId - The line currently drawn.
 * @param {string | null} objectSetId - The object set currently asked for.
 * @return {boolean} True when the response belongs to the current section.
 */
export const isCurrentSection = (
  response: CrossSectionResponse | undefined,
  sectionGeometryId: string | null,
  objectSetId: string | null
): boolean => {
  if (!response) {
    return false;
  }
  if (sectionGeometryId === null || objectSetId === null) {
    return false;
  }
  return (
    response.subject.sectionGeometryId === sectionGeometryId &&
    response.subject.objectSetId === objectSetId
  );
};

/**
 * How depths are read off a cut — a display choice over data already held, not a reason
 * to re-cut. `tvdss` and `tvd` are the true references; `flattened` lays a grid flat at
 * zero and plots the rest against it, winning over the reference.
 */
export type DepthHanging =
  | { kind: 'tvdss' }
  | { kind: 'tvd' }
  | { kind: 'flattened'; datumGridId: string };

/**
 * The datum surface's depth at each sampled distance, for hanging the section on it.
 *
 * Keyed by distance rather than by index: every grid on a section is sampled at the
 * same positions, so the two line up — but keying on the position says so, and a grid
 * that came back short cannot silently shift the whole stack.
 *
 * @param {Array} horizons - The grouped horizons.
 * @param {string} datumGridId - The grid the section hangs from.
 * @return {Map} Distance to the datum's TVDSS there.
 */
const datumDepths = (
  horizons: GroupedHorizon[],
  datumGridId: string
): Map<number, number> => {
  const datum = horizons.find((horizon) => horizon.gridId === datumGridId);
  const depths = new Map<number, number>();
  datum?.samples.forEach((sample) => {
    if (sample.tvdssFt !== null) {
      depths.set(sample.xFt, sample.tvdssFt);
    }
  });
  return depths;
};

/**
 * The horizon stack as profile series: one line per grid, in the order the backend
 * stacked them.
 *
 * Gaps are left in place as null depths — where the line breaks is the profile
 * plan's rule, and it is the same rule for every kind of series, so it lives there
 * rather than once per caller.
 *
 * @param {Array} horizons - The grouped horizons, already in stratigraphic order.
 * @param {DepthHanging} hanging - How depths are read.
 * @param {Function} colorForGrid - The grid's swatch, as the rail has it.
 * @return {Array} One profile series per grid.
 */
export const toProfileSeries = (
  horizons: GroupedHorizon[],
  hanging: DepthHanging,
  colorForGrid: (gridId: string) => string
): ProfileSeries[] => {
  // Built once for the stack rather than per grid: the datum is one surface, and
  // every grid is measured against the same copy of it.
  const datum =
    hanging.kind === 'flattened'
      ? datumDepths(horizons, hanging.datumGridId)
      : null;

  return horizons.map((horizon) => ({
    id: horizon.gridId,
    // The interval is what a geologist reads; a grid with none is named by the grid
    // itself rather than left blank.
    label: horizon.geologicInterval ?? horizon.gridName,
    color: colorForGrid(horizon.gridId),
    points: horizon.samples.map((sample) => ({
      distanceFt: sample.xFt,
      depthFt: plotDepth(sample, hanging, datum),
      // The pair the hover tooltip reads — the sample as it is, in each reference,
      // untouched by whatever hanging the plot is drawn in.
      tvdssFt: sample.tvdssFt,
      tvdFt: sample.tvdFt,
    })),
  }));
};

/**
 * One sample's plotted depth.
 *
 * Where the datum itself is missing the flattened depth is a gap, not zero: the
 * section cannot be measured against a surface that has no value there, and drawing
 * zero would put the horizon on the datum it is missing.
 *
 * @param {HorizonSample} sample - The sample.
 * @param {DepthHanging} hanging - How depths are read.
 * @param {Map | null} datum - The datum's depths, when hung on one.
 * @return {number | null} The depth to plot, or null for a gap.
 */
const plotDepth = (
  sample: { xFt: number; tvdssFt: number | null; tvdFt: number | null },
  hanging: DepthHanging,
  datum: Map<number, number> | null
): number | null => {
  if (hanging.kind === 'flattened') {
    const at = datum?.get(sample.xFt);
    if (at === undefined || sample.tvdssFt === null) {
      return null;
    }
    return sample.tvdssFt - at;
  }
  return hanging.kind === 'tvd' ? sample.tvdFt : sample.tvdssFt;
};

/**
 * The ground surface as a profile series.
 *
 * In TVDSS the ground line is its elevation. In TVD it is zero by definition —
 * that reference counts downward from ground level — so the line runs flat along
 * the top wherever the DEM has a value, and breaks where it does not.
 *
 * Hung on a datum there is no ground line at all: ground level has no fixed
 * relationship to the datum, so a line drawn against it would be measuring one thing
 * in another thing's units.
 *
 * @param {GroundSurfaceValue | null} ground - The ground surface, if it was asked for.
 * @param {DepthHanging} hanging - How depths are read.
 * @return {Array | null} The ground points, or null when there is no ground line.
 */
export const toGroundProfile = (
  ground: GroundSurfaceValue | null,
  hanging: DepthHanging
): ProfilePoint[] | null => {
  if (!ground || hanging.kind === 'flattened') {
    return null;
  }
  return ground.samples.map((sample) => ({
    distanceFt: sample.xFt,
    depthFt: groundDepth(sample.elevationFt, hanging.kind),
    // Ground level for the tooltip: the elevation itself, whatever the reference.
    tvdssFt: sample.elevationFt,
  }));
};

/**
 * The ground's plotted depth: its elevation in TVDSS, zero in TVD.
 *
 * @param {number | null} elevationFt - The DEM elevation, or null in a gap.
 * @param {string} kind - The depth reference.
 * @return {number | null} The depth to plot, or null for a gap.
 */
const groundDepth = (
  elevationFt: number | null,
  kind: 'tvdss' | 'tvd'
): number | null => {
  if (elevationFt === null) {
    return null;
  }
  return kind === 'tvd' ? 0 : elevationFt;
};

/**
 * The crossing wells as profile markers.
 *
 * One marker per crossing, not per well: a well that crosses the plane twice is at
 * two distances and both are real. The uwi rides along so every crossing of one well
 * can be emphasised together, since they are one well.
 *
 * The depth follows the hanging, like everything else on the plot — but the readout
 * quotes the true depths the response carries, which is why they are kept apart.
 *
 * @param {Array} wells - The grouped crossing wells.
 * @param {DepthHanging} hanging - How depths are read.
 * @param {Map} datumByDistance - The datum's depths, when hung on one.
 * @param {Function} colorForWell - The map's own well colour, so the two agree.
 * @return {Array} One crossing per row.
 */
export const toProfileCrossings = (
  wells: GroupedWell[],
  hanging: DepthHanging,
  datumByDistance: Map<number, number>,
  colorForWell: (well: GroupedWell) => string
): ProfileCrossing[] =>
  wells.map((well) => ({
    uwi: well.uwi,
    distanceFt: well.xOffsetFt,
    depthFt: crossingDepth(well, hanging, datumByDistance),
    // The class the standard symbology is keyed on.
    status: well.status,
    // The pair the hover tooltip reads, straight off the crossing.
    tvdssFt: well.tvdssFt,
    tvdFt: well.tvdFt,
    color: colorForWell(well),
    label: well.header.name,
    // The only field that separates a well placed by real geometry from one standing
    // at a nominal depth.
    geometrySourceKind: well.geometrySourceKind,
    workspaceEdited: well.workspaceEdited === true,
  }));

/**
 * A crossing's plotted depth. Flattening measures it against the datum at that
 * distance; without one it is the true depth the reference names.
 *
 * @param {GroupedWell} well - The crossing.
 * @param {DepthHanging} hanging - How depths are read.
 * @param {Map} datumByDistance - The datum's depths, when hung on one.
 * @return {number | null} The depth to plot, or null where it cannot be placed.
 */
const crossingDepth = (
  well: GroupedWell,
  hanging: DepthHanging,
  datumByDistance: Map<number, number>
): number | null => {
  if (hanging.kind === 'flattened') {
    const datum = nearestDatum(datumByDistance, well.xOffsetFt);
    if (datum === null || well.tvdssFt === null) {
      return null;
    }
    return well.tvdssFt - datum;
  }
  return hanging.kind === 'tvd' ? well.tvdFt : well.tvdssFt;
};

/**
 * The defining wells as bores to draw, one per vertex that stands on a well.
 *
 * Well to Well only: in Arbitrary Line nothing stands behind a vertex, so the fields
 * are null and this yields nothing. A well missing either end is skipped rather than
 * half-drawn — the backend leaves them null when it cannot resolve the well's KB, and
 * a stick with one end guessed would be a depth the data does not support.
 *
 * The ends share the well's own datum — KB at the top, the survey's toe at the bottom
 * — so under TVD the bore runs from zero to `surfaceElevationFt - tdTvdssFt`, the
 * depth of the toe below the well's own KB. That keeps it measured in one thing rather
 * than mixing the well's datum with a ground sample.
 *
 * @param {Array} vertexLabels - The subject's vertex labels.
 * @param {DepthHanging} hanging - How depths are read.
 * @param {Map} datumByDistance - The datum's depths, when hung on one.
 * @param {Function} colorForWell - The defining well's colour, as the rail has it.
 * @param {Function} [nameForWell] - What the pick knew the well as, for a cut that
 *   names it nothing.
 * @return {Array} One stick per defining well that can be placed.
 */
export const toProfileSticks = (
  vertexLabels: CrossSectionSubject['vertexLabels'],
  hanging: DepthHanging,
  datumByDistance: Map<number, number>,
  colorForWell: (uwi: string) => string,
  nameForWell?: (uwi: string) => string | null
): ProfileStickInput[] =>
  vertexLabels.flatMap((vertex, index) => {
    const { uwi, surfaceElevationFt, tdTvdssFt } = vertex;
    if (uwi === null || surfaceElevationFt === null || tdTvdssFt === null) {
      return [];
    }

    const ends = stickEnds(
      surfaceElevationFt,
      tdTvdssFt,
      hanging,
      datumByDistance,
      vertex.xFt
    );
    if (ends === null) {
      return [];
    }

    return [
      {
        uwi,
        distanceFt: vertex.xFt,
        topFt: ends.topFt,
        bottomFt: ends.bottomFt,
        color: colorForWell(uwi),
        // Counted over every vertex, not over the bores that could be placed: a well
        // the backend could not depth still holds its place in the traverse, and the
        // map's dot still numbers it.
        position: index + 1,
        // The cut names a defining well from its `wells` row, blank for a well with
        // neither lease name nor number — exactly the survey-less verticals now reaching
        // the section. A pick carries the map's tile label, so fall back to that.
        name: vertex.name ?? nameForWell?.(uwi) ?? null,
        status: vertex.status,
        // The hole's own length: its KB down to the survey's toe.
        totalDepthFt: surfaceElevationFt - tdTvdssFt,
      },
    ];
  });

/**
 * A stick's two ends in the plot's depth terms.
 *
 * @param {number} surfaceElevationFt - The well's KB.
 * @param {number} tdTvdssFt - Total depth, in TVDSS.
 * @param {DepthHanging} hanging - How depths are read.
 * @param {Map} datumByDistance - The datum's depths, when hung on one.
 * @param {number} distanceFt - Where the well stands along the section.
 * @return {object | null} The ends, or null where the stick cannot be placed.
 */
const stickEnds = (
  surfaceElevationFt: number,
  tdTvdssFt: number,
  hanging: DepthHanging,
  datumByDistance: Map<number, number>,
  distanceFt: number
): { topFt: number; bottomFt: number } | null => {
  if (hanging.kind === 'tvd') {
    // TVD is measured from the well's KB, so its own top is zero by definition.
    return { topFt: 0, bottomFt: surfaceElevationFt - tdTvdssFt };
  }
  if (hanging.kind === 'flattened') {
    const datum = nearestDatum(datumByDistance, distanceFt);
    if (datum === null) {
      return null;
    }
    return {
      topFt: surfaceElevationFt - datum,
      bottomFt: tdTvdssFt - datum,
    };
  }
  return { topFt: surfaceElevationFt, bottomFt: tdTvdssFt };
};

/**
 * The datum's depth nearest a distance. A crossing does not fall on a sample, so its
 * datum is the closest sampled one rather than an exact match.
 *
 * @param {Map} datumByDistance - The datum's depths, by sampled distance.
 * @param {number} distanceFt - The crossing's distance.
 * @return {number | null} The datum depth, or null when the datum has none nearby.
 */
const nearestDatum = (
  datumByDistance: Map<number, number>,
  distanceFt: number
): number | null => {
  let nearest: number | null = null;
  let nearestGap = Number.POSITIVE_INFINITY;
  datumByDistance.forEach((depth, at) => {
    const gap = Math.abs(at - distanceFt);
    if (gap < nearestGap) {
      nearestGap = gap;
      nearest = depth;
    }
  });
  return nearest;
};

/**
 * The datum surface's depths, for hanging crossings and readouts on it.
 *
 * @param {Array} horizons - The grouped horizons.
 * @param {DepthHanging} hanging - How depths are read.
 * @return {Map} Distance to the datum's TVDSS there, empty when not hung on one.
 */
export const datumDepthsFor = (
  horizons: GroupedHorizon[],
  hanging: DepthHanging
): Map<number, number> => {
  if (hanging.kind !== 'flattened') {
    return new Map();
  }
  return datumDepths(horizons, hanging.datumGridId);
};

/**
 * The fault crossings as profile spans.
 *
 * @param {Array} faults - The grouped faults.
 * @param {string} defaultColor - Colour for a fault whose own is not set.
 * @return {Array} One span per crossing.
 */
export const toProfileFaults = (
  faults: GroupedFault[],
  defaultColor: string
): ProfileFault[] =>
  faults.map((fault) => ({
    id: `${fault.id}-${fault.crossingIndex}`,
    name: fault.name,
    startFt: fault.startFt,
    endFt: fault.endFt,
    color: fault.color ?? defaultColor,
  }));

/**
 * The section's vertices as profile guides, in the along-line positions and with
 * the letters the map drew.
 *
 * @param {CrossSectionSubject} subject - The response's subject block.
 * @return {Array} One guide per vertex.
 */
export const toProfileVertices = (
  subject: CrossSectionSubject
): ProfileVertex[] =>
  subject.vertexLabels.map((vertex) => ({
    distanceFt: vertex.xFt,
    label: vertex.label,
  }));

/**
 * What a full-line cut reported about how finely it could be sampled. Held apart from
 * what the plot shows, because a refined answer reports the step it was given — asking
 * it whether to refine would flip the request back and forth for ever.
 */
export interface RefineBaseline {
  lengthFt: number;
  /** The step the whole line was sampled at. */
  stepFt: number;
  /** The finest the backend will go, whatever is asked for. */
  nativeResolutionFt: number;
}

/**
 * How much of the line has to be off-screen before a finer look is worth a round trip.
 */
const REFINE_SPAN_FRACTION = 0.8;

/**
 * The window to re-sample for the view the user has zoomed to, or null to leave the
 * request on the whole line.
 *
 * Two things have to be true. There must be detail to gain: a long line is sampled
 * coarser than the backend's native resolution, and only then does a window come back
 * finer — a short line already samples below native, so a window would come back
 * COARSER and the zoom would lose detail rather than gain it. And the user has to have
 * actually zoomed in, since re-cutting the whole line at the same step is a round trip
 * for nothing.
 *
 * The bounds are quantised to the step so that a pixel of panning does not invent a new
 * window, and clamped to the line so the backend is never asked about ground the
 * section does not cover.
 *
 * @param {Array | null} visible - The distance range on screen, if it is known.
 * @param {RefineBaseline | null} baseline - What the full-line cut reported.
 * @return {SectionViewport | null} The window to ask for, or null for the whole line.
 */
export const refineWindow = (
  visible: [number, number] | null,
  baseline: RefineBaseline | null
): SectionViewport | null => {
  if (visible === null || baseline === null) {
    return null;
  }
  const { lengthFt, stepFt, nativeResolutionFt } = baseline;
  if (lengthFt <= 0 || stepFt <= nativeResolutionFt) {
    return null;
  }

  const start = Math.max(0, Math.min(visible[0], visible[1]));
  const end = Math.min(lengthFt, Math.max(visible[0], visible[1]));
  const span = end - start;
  if (span <= 0 || span > lengthFt * REFINE_SPAN_FRACTION) {
    return null;
  }

  const grain = Math.max(stepFt, 1);
  return {
    startFt: Math.max(0, Math.floor(start / grain) * grain),
    endFt: Math.min(lengthFt, Math.ceil(end / grain) * grain),
  };
};
